export const SUITS = [
  { id: "clubs", name: "Trèfle", symbol: "♣", red: false },
  { id: "diamonds", name: "Carreau", symbol: "♦", red: true },
  { id: "hearts", name: "Cœur", symbol: "♥", red: true },
  { id: "spades", name: "Pique", symbol: "♠", red: false },
];

export const RANKS = ["7", "8", "9", "J", "Q", "K", "10", "A"];

const TRUMP_ORDER = ["7", "8", "Q", "K", "10", "A", "9", "J"];
const PLAIN_ORDER = ["7", "8", "9", "J", "Q", "K", "10", "A"];
const TRUMP_POINTS = { "7": 0, "8": 0, Q: 3, K: 4, "10": 10, A: 11, "9": 14, J: 20 };
const PLAIN_POINTS = { "7": 0, "8": 0, "9": 0, J: 2, Q: 3, K: 4, "10": 10, A: 11 };
const POSITION_TO_SEAT = { "1": 0, "2": 1, "3": 2, D: 3 };

export const DECK = SUITS.flatMap((suit) =>
  RANKS.map((rank) => Object.freeze({ id: `${suit.id}-${rank}`, suit: suit.id, rank })),
);

function domainError(code) {
  const error = new Error(code);
  error.code = code;
  return error;
}

export function cardPoints(card, trump) {
  return card.suit === trump ? TRUMP_POINTS[card.rank] : PLAIN_POINTS[card.rank];
}

export function cardOrder(card, trump) {
  const order = card.suit === trump ? TRUMP_ORDER : PLAIN_ORDER;
  return order.indexOf(card.rank);
}

export function sameTeam(seatA, seatB) {
  return seatA % 2 === seatB % 2;
}

export function winningPlay(trick, trump) {
  if (!trick.length) return null;
  const leadSuit = trick[0].card.suit;
  let winner = trick[0];

  for (let index = 1; index < trick.length; index += 1) {
    const challenger = trick[index];
    const winnerTrump = winner.card.suit === trump;
    const challengerTrump = challenger.card.suit === trump;

    if (challengerTrump && !winnerTrump) {
      winner = challenger;
      continue;
    }
    if (winnerTrump && !challengerTrump) continue;

    if (challenger.card.suit === winner.card.suit) {
      if (cardOrder(challenger.card, trump) > cardOrder(winner.card, trump)) winner = challenger;
      continue;
    }

    if (!winnerTrump && challenger.card.suit === leadSuit && winner.card.suit !== leadSuit) {
      winner = challenger;
    }
  }

  return winner;
}

export function getLegalCards(hand, trick, trump, seat) {
  if (!trick.length) return [...hand];

  const leadSuit = trick[0].card.suit;
  const cardsInLeadSuit = hand.filter((card) => card.suit === leadSuit);
  const currentWinner = winningPlay(trick, trump);

  if (cardsInLeadSuit.length) {
    if (leadSuit !== trump) return cardsInLeadSuit;

    const higherTrumps = cardsInLeadSuit.filter(
      (card) => cardOrder(card, trump) > cardOrder(currentWinner.card, trump),
    );
    return higherTrumps.length ? higherTrumps : cardsInLeadSuit;
  }

  if (sameTeam(currentWinner.seat, seat)) return [...hand];

  const trumps = hand.filter((card) => card.suit === trump);
  if (!trumps.length) return [...hand];

  if (currentWinner.card.suit === trump) {
    const higherTrumps = trumps.filter(
      (card) => cardOrder(card, trump) > cardOrder(currentWinner.card, trump),
    );
    return higherTrumps.length ? higherTrumps : trumps;
  }

  return trumps;
}

function seenRank(played, suit, rank) {
  return played.some((play) => play.card.suit === suit && play.card.rank === rank);
}

function futureValue(card, trump, played) {
  const points = cardPoints(card, trump);
  const order = cardOrder(card, trump);
  let value = points * 1.7 + order * 0.7;

  if (card.suit === trump) value += 7 + order * 0.8;
  if (card.suit !== trump && card.rank === "A") value += 9;
  if (card.suit !== trump && card.rank === "10") {
    value += seenRank(played, card.suit, "A") ? 8 : 2;
  }
  return value;
}

function leadScore(card, hand, trump, played, isTakingTeam, random) {
  const sameSuit = hand.filter((item) => item.suit === card.suit);
  const trumpCards = hand.filter((item) => item.suit === trump);
  let score = random() * 2.5;

  if (card.suit === trump) {
    const hasMasterTrump = trumpCards.some((item) => item.rank === "J");
    const drawTrumps = trumpCards.length >= 3 && (hasMasterTrump || isTakingTeam);
    score += drawTrumps ? 18 + cardOrder(card, trump) * 2.8 : -10 + cardOrder(card, trump);
    if (trumpCards.length === 1) score -= 14;
    return score;
  }

  if (card.rank === "A") score += 42;
  else if (card.rank === "10" && seenRank(played, card.suit, "A")) score += 34;
  else if (card.rank === "K" && seenRank(played, card.suit, "A") && seenRank(played, card.suit, "10")) score += 25;
  else score += cardOrder(card, trump) * 1.5;

  if (sameSuit.length === 1 && cardPoints(card, trump) <= 4) score += 9;
  if (sameSuit.some((item) => item.rank === "A") && card.rank === "10") score += 7;
  return score;
}

export function chooseCard({ hand, trick, trump, seat, played, takingTeam, random }) {
  const legal = getLegalCards(hand, trick, trump, seat);

  if (!trick.length) {
    return legal.reduce((best, card) =>
      leadScore(card, hand, trump, played, seat % 2 === takingTeam, random) >
      leadScore(best, hand, trump, played, seat % 2 === takingTeam, random)
        ? card
        : best,
    );
  }

  const before = winningPlay(trick, trump);
  const partnerWinning = sameTeam(before.seat, seat);
  const isLast = trick.length === 3;

  if (!partnerWinning) {
    const winningCards = legal.filter((card) => winningPlay([...trick, { seat, card }], trump).seat === seat);
    if (winningCards.length) {
      return winningCards.reduce((cheapest, card) => {
        const cost = futureValue(card, trump, played) + random() * 0.5;
        const cheapestCost = futureValue(cheapest, trump, played) + random() * 0.5;
        return cost < cheapestCost ? card : cheapest;
      });
    }
  }

  if (partnerWinning && isLast) {
    return legal.reduce((best, card) =>
      cardPoints(card, trump) > cardPoints(best, trump) ? card : best,
    );
  }

  return legal.reduce((discard, card) => {
    const value = futureValue(card, trump, played) + random() * 1.4;
    const discardValue = futureValue(discard, trump, played) + random() * 1.4;
    return value < discardValue ? card : discard;
  });
}

export function beloteTeam(hands, trump) {
  for (let seat = 0; seat < hands.length; seat += 1) {
    const hasQueen = hands[seat].some((card) => card.suit === trump && card.rank === "Q");
    const hasKing = hands[seat].some((card) => card.suit === trump && card.rank === "K");
    if (hasQueen && hasKing) return seat % 2;
  }
  return null;
}

export function playDeal(sourceHands, trump, userSeat, random = Math.random) {
  const hands = sourceHands.map((hand) => [...hand]);
  const takingTeam = userSeat % 2;
  const bonusTeam = beloteTeam(hands, trump);
  const teamPoints = [0, 0];
  const teamTricks = [0, 0];
  const played = [];
  let leader = 0;

  for (let trickIndex = 0; trickIndex < 8; trickIndex += 1) {
    const trick = [];

    for (let offset = 0; offset < 4; offset += 1) {
      const seat = (leader + offset) % 4;
      const card = chooseCard({
        hand: hands[seat],
        trick,
        trump,
        seat,
        played,
        takingTeam,
        random,
      });
      hands[seat].splice(hands[seat].indexOf(card), 1);
      trick.push({ seat, card });
    }

    const winner = winningPlay(trick, trump);
    const trickPoints = trick.reduce((total, play) => total + cardPoints(play.card, trump), 0);
    const winningTeam = winner.seat % 2;
    teamPoints[winningTeam] += trickPoints + (trickIndex === 7 ? 10 : 0);
    teamTricks[winningTeam] += 1;
    played.push(...trick);
    leader = winner.seat;
  }

  const scoredPoints = [...teamPoints];
  if (bonusTeam !== null) scoredPoints[bonusTeam] += 20;
  const defendingTeam = 1 - takingTeam;

  return {
    success: scoredPoints[takingTeam] > scoredPoints[defendingTeam],
    points: scoredPoints[takingTeam],
    rawPoints: teamPoints[takingTeam],
    tricks: teamTricks[takingTeam],
    belote: bonusTeam === takingTeam,
  };
}

function takeStrength(initialHand, center, trump) {
  const known = [...initialHand, center];
  const trumpWeights = { J: 5.4, "9": 4.6, A: 3.1, "10": 2.5, K: 2.0, Q: 1.6, "8": 0.5, "7": 0.35 };
  const trumps = known.filter((card) => card.suit === trump);
  let strength = trumps.reduce((total, card) => total + trumpWeights[card.rank], 0);
  strength += Math.max(0, trumps.length - 2) * 1.15;

  for (const suit of SUITS) {
    if (suit.id === trump) continue;
    const cards = known.filter((card) => card.suit === suit.id);
    if (cards.some((card) => card.rank === "A")) strength += 2.25;
    if (cards.some((card) => card.rank === "A") && cards.some((card) => card.rank === "10")) strength += 0.85;
  }

  if (trumps.some((card) => card.rank === "K") && trumps.some((card) => card.rank === "Q")) strength += 1.8;
  return strength;
}

function takeProbability(initialHand, center, trump) {
  const strength = takeStrength(initialHand, center, trump);
  const probability = 1 / (1 + Math.exp(-(strength - 8.15) / 1.2));
  return Math.min(0.96, Math.max(0.025, probability));
}

function scenarioWeight(initialHands, center, trump, userSeat) {
  const centerSuit = center.suit;
  let weight = 1;

  if (trump === centerSuit) {
    for (let seat = 0; seat < userSeat; seat += 1) {
      weight *= 1 - takeProbability(initialHands[seat], center, centerSuit);
    }
    return weight;
  }

  for (let seat = 0; seat < 4; seat += 1) {
    if (seat === userSeat) continue;
    weight *= 1 - takeProbability(initialHands[seat], center, centerSuit);
  }

  const otherSuits = SUITS.map((suit) => suit.id).filter((suit) => suit !== centerSuit);
  for (let seat = 0; seat < userSeat; seat += 1) {
    const strongestCall = Math.max(
      ...otherSuits.map((suit) => takeProbability(initialHands[seat], center, suit)),
    );
    weight *= Math.max(0.035, 1 - strongestCall);
  }

  return weight;
}

function hashText(text) {
  let hash = 2166136261;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function mulberry32(seed) {
  let value = seed >>> 0;
  return function random() {
    value += 0x6d2b79f5;
    let result = value;
    result = Math.imul(result ^ (result >>> 15), result | 1);
    result ^= result + Math.imul(result ^ (result >>> 7), result | 61);
    return ((result ^ (result >>> 14)) >>> 0) / 4294967296;
  };
}

function shuffled(cards, random) {
  const result = [...cards];
  for (let index = result.length - 1; index > 0; index -= 1) {
    const target = Math.floor(random() * (index + 1));
    [result[index], result[target]] = [result[target], result[index]];
  }
  return result;
}

export function dealUnknownCards(hand, center, userSeat, random = Math.random) {
  const knownIds = new Set([...hand, center].map((card) => card.id));
  const pool = shuffled(
    DECK.filter((card) => !knownIds.has(card.id)),
    random,
  );
  const initialHands = [[], [], [], []];
  initialHands[userSeat] = [...hand];
  let cursor = 0;

  for (let seat = 0; seat < 4; seat += 1) {
    if (seat === userSeat) continue;
    initialHands[seat] = pool.slice(cursor, cursor + 5);
    cursor += 5;
  }

  const finalHands = initialHands.map((cards) => [...cards]);
  finalHands[userSeat].push(center, ...pool.slice(cursor, cursor + 2));
  cursor += 2;

  for (let seat = 0; seat < 4; seat += 1) {
    if (seat === userSeat) continue;
    finalHands[seat].push(...pool.slice(cursor, cursor + 3));
    cursor += 3;
  }

  return { initialHands, finalHands };
}

function summarizeAccumulator(accumulator) {
  const probability = accumulator.sumWeight
    ? (accumulator.successWeight / accumulator.sumWeight) * 100
    : 0;
  const effectiveSamples = accumulator.sumWeightSquared
    ? (accumulator.sumWeight * accumulator.sumWeight) / accumulator.sumWeightSquared
    : 0;
  const proportion = probability / 100;
  const ci95 = effectiveSamples
    ? 1.96 * Math.sqrt((proportion * (1 - proportion)) / effectiveSamples) * 100
    : 0;

  return {
    probability,
    expectedPoints: accumulator.pointsWeight / accumulator.sumWeight,
    expectedTricks: accumulator.tricksWeight / accumulator.sumWeight,
    ci95,
    effectiveSamples,
  };
}

function nextPaint() {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame === "function") requestAnimationFrame(() => resolve());
    else setTimeout(resolve, 0);
  });
}

export async function analyzeTakingOptions({ hand, center, position, iterations = 5000, onProgress }) {
  if (hand.length !== 5) throw domainError("hand-size");
  if (!center) throw domainError("center-required");
  if (hand.some((card) => card.id === center.id)) throw domainError("duplicate-card");
  if (!(position in POSITION_TO_SEAT)) throw domainError("unknown-position");

  const userSeat = POSITION_TO_SEAT[position];
  const seedText = `${hand.map((card) => card.id).sort().join("|")}:${center.id}:${position}`;
  const seed = hashText(seedText);
  const dealRandom = mulberry32(seed);
  const accumulators = Object.fromEntries(
    SUITS.map((suit) => [
      suit.id,
      { sumWeight: 0, sumWeightSquared: 0, successWeight: 0, pointsWeight: 0, tricksWeight: 0 },
    ]),
  );
  const chunkSize = iterations >= 10000 ? 125 : 100;

  for (let start = 0; start < iterations; start += chunkSize) {
    const end = Math.min(iterations, start + chunkSize);
    for (let iteration = start; iteration < end; iteration += 1) {
      const deal = dealUnknownCards(hand, center, userSeat, dealRandom);

      for (const suit of SUITS) {
        const weight = scenarioWeight(deal.initialHands, center, suit.id, userSeat);
        const playRandom = mulberry32(hashText(`${seed}:${iteration}:${suit.id}`));
        const outcome = playDeal(deal.finalHands, suit.id, userSeat, playRandom);
        const accumulator = accumulators[suit.id];
        accumulator.sumWeight += weight;
        accumulator.sumWeightSquared += weight * weight;
        accumulator.successWeight += weight * (outcome.success ? 1 : 0);
        accumulator.pointsWeight += weight * outcome.points;
        accumulator.tricksWeight += weight * outcome.tricks;
      }
    }

    onProgress?.(end / iterations);
    await nextPaint();
  }

  const bySuit = Object.fromEntries(
    SUITS.map((suit) => [suit.id, { suit: suit.id, ...summarizeAccumulator(accumulators[suit.id]) }]),
  );
  const alternatives = SUITS.filter((suit) => suit.id !== center.suit)
    .map((suit) => bySuit[suit.id])
    .sort((a, b) => b.probability - a.probability);

  return {
    returned: bySuit[center.suit],
    bestAlternative: alternatives[0],
    alternatives,
    bySuit,
    iterations,
    position,
  };
}
